// pdf.js, loaded on demand (it's the biggest dependency). The worker and its data files are served from our own
// origin (CSP worker-src / connect-src).
import type { PDFDocumentProxy } from "pdfjs-dist";

let lib: Promise<typeof import("pdfjs-dist")> | null = null;
function pdfjs() {
  lib ??= import("pdfjs-dist").then((m) => {
    // Copied by scripts/copy-pdfjs.mjs; the version query busts the one-day cache on upgrades.
    m.GlobalWorkerOptions.workerSrc = `/pdfjs/pdf.worker.min.js?v=${m.version}`;
    return m;
  });
  return lib;
}

/** Loads a PDF; call `destroy()` when done to stop the worker's copy. */
export async function loadPdf(url: string): Promise<{ doc: PDFDocumentProxy; destroy: () => Promise<void> }> {
  const m = await pdfjs();
  const task = m.getDocument({
    url,
    disableRange: true,
    disableStream: true,
    enableXfa: false,
    cMapUrl: "/pdfjs/cmaps/",
    cMapPacked: true,
    standardFontDataUrl: "/pdfjs/standard_fonts/",
    wasmUrl: "/pdfjs/wasm/",
    iccUrl: "/pdfjs/iccs/",
  });
  return { doc: await task.promise, destroy: () => task.destroy() };
}

/** Renders one page into a canvas at `cssWidth` pixels wide (sharp on high-DPI screens). */
export async function renderPage(doc: PDFDocumentProxy, index: number, canvas: HTMLCanvasElement, cssWidth: number) {
  const page = await doc.getPage(index + 1);
  const base = page.getViewport({ scale: 1 });
  const scale = cssWidth / base.width;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const vp = page.getViewport({ scale: scale * dpr });
  canvas.width = Math.floor(vp.width);
  canvas.height = Math.floor(vp.height);
  canvas.style.width = `${cssWidth}px`;
  canvas.style.height = `${Math.floor(vp.height / dpr)}px`;
  const task = page.render({ canvas, viewport: vp });
  await task.promise;
  return { width: cssWidth, height: vp.height / dpr };
}
